import { useOasGraphLazy } from '@squareup/dex-data-access-oas';
import { getDeveloperApi } from '@squareup/dex-data-shared-developer-api';
import { MarkdownIgnitionCodeBlock } from '@squareup/dex-feature-shared-markdown-fence';
import { Box, Paragraph10 } from '@squareup/dex-ui-shared-base';
import React, { FC, useMemo } from 'react';

import {
  parseEndpointTag,
  resolveCodegenInput,
  specOptionsFor,
} from './endpoint-request-snippet-utils';

interface MdxEndpointRequestSnippetProps {
  // The API version, e.g. "latest" or "2025-09-24". Maps onto our
  // SpecificationVersionInfo.version field — undefined ("latest") fetches
  // the current published spec.
  api?: string;
  // The HTTP verb + path Fern uses in its MDX source, e.g.
  // "GET /v2/channels". We split this into method + path and look up the
  // graph endpoint whose own httpMethod + path render identically.
  endpoint: string;
  // Optional scenario name (Fern-internal authoring metadata) used to
  // pick a specific example body for the snippet. Falls back to the
  // first scenario, or the endpoint's default codegen input.
  example?: string;
}

const MdxEndpointRequestSnippet: FC<MdxEndpointRequestSnippetProps> = ({
  api,
  endpoint: endpointTag,
  example,
}) => {
  const parsed = useMemo(() => parseEndpointTag(endpointTag), [endpointTag]);
  const specOptions = useMemo(() => specOptionsFor(api), [api]);
  const { graph } = useOasGraphLazy(specOptions, getDeveloperApi());

  const resolved = useMemo(() => {
    if (!graph || !parsed) {
      return null;
    }
    const match = graph
      .listEndpoints()
      .find(
        (e) =>
          e.httpMethod.toUpperCase() === parsed.method && e.path === parsed.path
      );
    if (!match) {
      return null;
    }
    return {
      endpoint: match,
      codegenInput: resolveCodegenInput(match, example),
    };
  }, [graph, parsed, example]);

  if (!parsed) {
    // Author wrote an unparseable endpoint tag (no space between method
    // and path). Surface it as a tiny inline warning rather than failing
    // the whole page render.
    return (
      <Box>
        <Paragraph10>
          Could not parse endpoint: <code>{endpointTag}</code>
        </Paragraph10>
      </Box>
    );
  }

  // While the graph is loading or the endpoint isn't resolved yet, fall
  // through to MarkdownIgnitionCodeBlock which renders its own loading
  // shell. Once `resolved` lands, content + language reflect the real
  // endpoint and ignition takes over.
  const language = resolved ? `endpoint:${resolved.endpoint.id}` : 'endpoint:';
  const content = resolved ? JSON.stringify(resolved.codegenInput) : '{}';

  // Hand off our resolved spec version and endpoint identity explicitly
  // so MarkdownIgnitionCodeBlock doesn't fall back to its router-derived
  // "latest / unpublished" defaults. Without these overrides a page
  // like <EndpointRequestSnippet api="2025-09-24" ...> would silently
  // render the latest-spec snippet because the dispatcher re-derives
  // version from useRouter.
  return (
    <MarkdownIgnitionCodeBlock
      content={content}
      language={language}
      specOptionsOverride={specOptions}
      endpointNameOverride={resolved?.endpoint.id}
      lineNumbers
      openInEditor={false}
    />
  );
};

MdxEndpointRequestSnippet.displayName = 'MdxEndpointRequestSnippet';

export { MdxEndpointRequestSnippet };
export { parseEndpointTag } from './endpoint-request-snippet-utils';
